import { useMemo, useState } from 'react';
import { ShoppingCart, AlertTriangle } from 'lucide-react';
import { type StackCompound } from '../../data/peptidePresets';
import { calcCompound } from '../../utils/peptideCalculations';

const DURATIONS = [4, 8, 12, 24];

interface Props {
  stack: StackCompound[];
}

interface Line {
  id: number;
  name: string;
  productName?: string;
  productId?: string;
  vials: number | null;
  unitPrice?: number;
  lineTotal: number | null;
  outOfStock?: boolean;
  weeksPerVial: number | null;
}

/**
 * Turns the computed stack into a purchasable shopping list: for the chosen
 * treatment duration, how many vials of each real SKU are needed and the cost.
 * Preview-only for admins (no order is placed); the resolve/quantity logic here
 * is what a future "Create order" action would submit.
 */
export function OrderPreview({ stack }: Props) {
  const [weeks, setWeeks] = useState(12);

  const lines = useMemo<Line[]>(() => stack.map(c => {
    const r = calcCompound(c);
    const wpv = Number.isFinite(r.wpv) && r.wpv > 0 ? r.wpv : null;
    const vials = wpv ? Math.max(1, Math.ceil(weeks / wpv)) : null;
    const lineTotal = vials != null && typeof c.unitPrice === 'number' ? vials * c.unitPrice : null;
    return {
      id: c.id,
      name: c.name,
      productName: c.productName,
      productId: c.productId,
      vials,
      unitPrice: c.unitPrice,
      lineTotal,
      outOfStock: c.outOfStock,
      weeksPerVial: wpv,
    };
  }), [stack, weeks]);

  const linked = lines.filter(l => l.productId);
  const unlinked = lines.filter(l => !l.productId);
  const grandTotal = linked.reduce((sum, l) => sum + (l.lineTotal || 0), 0);
  const anyOos = linked.some(l => l.outOfStock);
  const anyMissingPrice = linked.some(l => l.lineTotal == null);

  if (!stack.length) return null;

  return (
    <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold flex items-center gap-2">
          <ShoppingCart className="w-4 h-4" />
          Order Preview
        </h2>
        <div className="flex items-center gap-1">
          {DURATIONS.map(d => (
            <button
              key={d}
              onClick={() => setWeeks(d)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-all ${
                weeks === d
                  ? 'bg-blue-500/10 border-blue-500/25 text-blue-600 dark:text-blue-400'
                  : 'border-neutral-200 dark:border-neutral-700 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'
              }`}
            >
              {d} wk
            </button>
          ))}
        </div>
      </div>

      {linked.length === 0 ? (
        <p className="text-[11px] text-neutral-400">
          No compounds are linked to a catalog SKU yet. Pick products from the formulary to see an orderable preview.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {linked.map(l => (
            <div key={l.id} className="flex items-center justify-between px-3 py-2 bg-neutral-50 dark:bg-neutral-900/50 rounded-md">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-neutral-700 dark:text-neutral-200 truncate">
                  {l.productName || l.name}
                  {l.outOfStock && (
                    <span className="ml-1.5 text-[9px] text-rose-500 font-semibold">OUT OF STOCK</span>
                  )}
                </div>
                <div className="text-[10px] text-neutral-400">
                  {l.vials != null ? `${l.vials} vial${l.vials !== 1 ? 's' : ''}` : 'qty n/a'}
                  {l.weeksPerVial != null && ` · ~${l.weeksPerVial.toFixed(1)} wk/vial`}
                  {typeof l.unitPrice === 'number' && ` · $${l.unitPrice.toFixed(2)}/vial`}
                </div>
              </div>
              <div className="font-mono text-[13px] font-semibold text-emerald-600 dark:text-emerald-400 shrink-0">
                {l.lineTotal != null ? `$${l.lineTotal.toFixed(2)}` : '—'}
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between px-3 py-2 mt-1 border-t border-neutral-200 dark:border-neutral-700">
            <span className="text-[12px] font-semibold text-neutral-500 uppercase tracking-wider">
              Est. total · {weeks} wk
            </span>
            <span className="font-mono text-base font-bold text-neutral-800 dark:text-neutral-100">
              ${grandTotal.toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {unlinked.length > 0 && (
        <p className="text-[10px] text-neutral-400 mt-3 flex items-start gap-1">
          <AlertTriangle className="w-3 h-3 mt-px text-amber-500 shrink-0" />
          <span>
            {unlinked.length} compound{unlinked.length !== 1 ? 's' : ''} ({unlinked.map(l => l.name).join(', ')}) {unlinked.length !== 1 ? 'are' : 'is'} manual entries not linked to a catalog SKU, so {unlinked.length !== 1 ? 'they’re' : 'it’s'} excluded from the total.
          </span>
        </p>
      )}

      {(anyOos || anyMissingPrice) && (
        <p className="text-[10px] text-amber-500 mt-2">
          {anyOos && 'Some items are out of stock. '}
          {anyMissingPrice && 'Some items have no catalog price. '}
          This is a preview — no order is placed.
        </p>
      )}
    </div>
  );
}
